import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, LoadingSkeleton, DeniedState } from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Send, Copy, UserX, UserCheck } from "lucide-react";

const ROLES = ["Admin", "RiskManager", "RiskOwner", "Approver", "Viewer"];

export default function Users() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [invOpen, setInvOpen] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/users"); setItems(data); }
    catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (user?.role !== "Admin") return <div className="max-w-7xl mx-auto px-6 py-10"><DeniedState requiredRoles={["Admin"]} /></div>;

  const changeRole = async (uid, role) => {
    try { await api.patch(`/users/${uid}`, { role }); toast.success("Role updated"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const toggleSuspend = async (u) => {
    const action = u.suspended ? "unsuspend" : "suspend";
    if (!u.suspended && !window.confirm(`Suspend ${u.email}? They will be signed out immediately and locked out until reinstated.`)) return;
    try { await api.post(`/admin/users/${u.id}/${action}`); toast.success(u.suspended ? "User reinstated" : "User suspended"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <PageHeader kicker="Users" title="Users & Roles" description="Manage tenant users, invite new members, and set roles." actions={<Button className="bg-[#0A1329] hover:bg-[#162347] text-white" onClick={() => setInvOpen(true)} data-testid="btn-invite-user"><Send className="h-4 w-4 mr-1.5" /> Invite user</Button>} />
      {loading ? <LoadingSkeleton /> : (
        <Card className="overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b"><tr className="text-left">
              {["Name", "Email", "Role", "Status", user?.is_super_admin ? "" : null].filter(Boolean).map((h) => <th key={h} className="px-4 py-2.5 text-[10px] uppercase tracking-widest font-mono font-semibold text-slate-500">{h}</th>)}
            </tr></thead>
            <tbody>
              {items.map((u) => (
                <tr key={u.id} className={`border-b border-slate-100 ${u.suspended ? "bg-rose-50/50" : ""}`} data-testid={`row-user-${u.email}`}>
                  <td className="px-4 py-3 font-semibold text-slate-900">{u.name}</td>
                  <td className="px-4 py-3 text-slate-700 text-xs font-mono">{u.email}</td>
                  <td className="px-4 py-3">
                    <Select value={u.role} onValueChange={(v) => changeRole(u.id, v)} disabled={u.id === user.id || u.suspended}>
                      <SelectTrigger className="w-[160px]" data-testid={`role-select-${u.email}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                    </Select>
                  </td>
                  <td className="px-4 py-3">
                    {u.suspended ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-mono font-semibold bg-rose-100 text-rose-800 border border-rose-200" data-testid={`suspended-${u.email}`}>SUSPENDED</span>
                    ) : u.is_super_admin ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-mono font-semibold bg-amber-50 text-amber-800 border border-amber-200">SUPER ADMIN</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-mono font-semibold bg-emerald-50 text-emerald-800 border border-emerald-200">ACTIVE</span>
                    )}
                  </td>
                  {user?.is_super_admin && (
                    <td className="px-4 py-3 text-right">
                      {u.id === user.id || u.is_super_admin ? (
                        <span className="text-[10px] font-mono text-slate-400">—</span>
                      ) : u.suspended ? (
                        <Button size="sm" variant="outline" className="text-emerald-700 hover:text-emerald-800 border-emerald-300" onClick={() => toggleSuspend(u)} data-testid={`btn-unsuspend-${u.email}`}>
                          <UserCheck className="h-3.5 w-3.5 mr-1" /> Reinstate
                        </Button>
                      ) : (
                        <Button size="sm" variant="outline" className="text-rose-700 hover:text-rose-800 border-rose-300" onClick={() => toggleSuspend(u)} data-testid={`btn-suspend-${u.email}`}>
                          <UserX className="h-3.5 w-3.5 mr-1" /> Suspend
                        </Button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
      {invOpen && (
        <InviteDialog onClose={() => setInvOpen(false)} onCreated={(res) => { setInvOpen(false); setInviteResult(res); load(); }} />
      )}
      {inviteResult && (
        <Dialog open={true} onOpenChange={() => setInviteResult(null)}>
          <DialogContent className="max-w-lg" data-testid="invite-result">
            <DialogHeader><DialogTitle className="font-display">Invitation created</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="text-sm text-slate-600">Email delivered — copy this link too as a backup for <span className="font-semibold">{inviteResult.invitation.email}</span>:</div>
              <div className="rounded-lg border border-cyan-300 bg-cyan-50 p-3 font-mono text-xs break-all" data-testid="invite-link">{inviteResult.invite_link}</div>
              <Button className="w-full" onClick={() => { navigator.clipboard.writeText(inviteResult.invite_link); toast.success("Link copied"); }} data-testid="invite-copy"><Copy className="h-4 w-4 mr-1.5" /> Copy link</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function InviteDialog({ onClose, onCreated }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("Viewer");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!email) return toast.error("Email required");
    setBusy(true);
    try { const { data } = await api.post("/invitations", { email, name: name || null, role }); toast.success("Invitation sent"); onCreated?.(data); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-md" data-testid="invite-dialog">
        <DialogHeader><DialogTitle className="font-display">Invite user</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Email</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="invite-email" /></div>
          <div><Label>Name (optional)</Label><Input value={name} onChange={(e) => setName(e.target.value)} /></div>
          <div><Label>Role</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger data-testid="invite-role"><SelectValue /></SelectTrigger>
              <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-[#0A1329] hover:bg-[#162347] text-white" data-testid="invite-send">Create invitation</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
